/// <amd-module name="TFS/Admin/Views/ProfileSettings/AuthorizationsView/AuthorizationsView" />
import React = require("react");
import * as Resources from "TFS/Admin/Views/ProfileSettings/AuthorizationsView/Resources";
import * as String from "VSS/Core/Util/String";

import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { ApplicationDataPanelView } from "TFS/Admin/Views/ProfileSettings/AuthorizationsView/Components/ApplicationDataPanelView";
import { AuthorizationsTableView } from "TFS/Admin/Views/ProfileSettings/AuthorizationsView/Components/AuthorizationsTableView";
import { RevokeAppAuthDialog } from "TFS/Admin/Views/ProfileSettings/AuthorizationsView/Components/RevokeAppAuthDialog";
import { IAuthorizationViewModel } from "TFS/Admin/Views/ProfileSettings/AuthorizationsView/Contracts/DataModel";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { AuthorizationDetails } from "VSS/Platform/Contracts/DelegatedAuthorization";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Header, HeaderDescription, TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
interface IAuthorizationsViewData {
    authorizations: IAuthorizationViewModel[];
    authRegisterAppUrl: string;
}

const authorizationsDataProviderId = "ms.vss-admin-web.authorizations-view-data-provider";
const revokeAuthDataProviderId = "ms.vss-admin-web.revoke-authorizations-data-provider";

class AuthorizationsViewComponent extends VssComponent<{}, {}> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private messagesService: IGlobalMessagesService;

    private _authData: IObservableArray<IAuthorizationViewModel> = new ObservableArray<IAuthorizationViewModel>([]);
    private _appToRevoke: IAuthorizationViewModel = {} as IAuthorizationViewModel;
    private _selectedRowAppData: any;

    private _showRevokeDialog: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _showApplicationDataPanel: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private readonly data: IAuthorizationsViewData;

    public constructor(props: {}, context?: any) {
        super(props, context);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.data = contributionService.getData<IAuthorizationsViewData>(authorizationsDataProviderId)!;

        if (this.data) {
            this._authData = new ObservableArray<IAuthorizationViewModel>(this.data.authorizations);
        }

        this.messagesService = this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
    }

    public componentDidMount() {
        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.Authorizations);

        const authCount = this.data && this.data.authorizations ? this.data.authorizations.length : 0;
        this._startActionCapture("Fetched Authorizations", { count: authCount });
    }

    public render() {
        if (!this.data) {
            return (
                <div className="auth-page-content">
                    <MessageCard className="auth-card-content" severity={MessageCardSeverity.Error}>
                        {Resources.DataProviderError}
                    </MessageCard>
                </div>
            );
        }

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="auth-page-content">
                    <Header title={Resources.AuthorizationsHeader} titleSize={TitleSize.Large} />
                    {this.data.authRegisterAppUrl ? (
                        <HeaderDescription className="auth-description">
                            <FormatComponent format={Resources.AuthHeaderDescriptionWithAppRegisterLink} elementType="div">
                                <Link href={this.data.authRegisterAppUrl} target="_blank" rel="noopener noreferrer" aria-label={Resources.clickHere}>
                                    {Resources.clickHere}
                                </Link>
                            </FormatComponent>
                        </HeaderDescription>
                    ) : (
                        <HeaderDescription className="auth-description">{Resources.AuthHeaderDescription}</HeaderDescription>
                    )}

                    <AuthorizationsTableView
                        authDataItemProvider={this._authData}
                        getAppRowData={this._getAppRowData.bind(this)}
                        onRevokeClick={this._onRevokeClick.bind(this)}
                    />
                </Page>

                <Observer showApplicationDataPanel={this._showApplicationDataPanel}>
                    {(props: { showApplicationDataPanel: boolean }) => {
                        return (
                            props.showApplicationDataPanel && (
                                <ApplicationDataPanelView
                                    onDismiss={this._dismissApplicationDataPanel.bind(this)}
                                    selectedRow={this._selectedRowAppData}
                                />
                            )
                        );
                    }}
                </Observer>

                <Observer showRevokeDialog={this._showRevokeDialog}>
                    {(props: { showRevokeDialog: boolean }) => {
                        return (
                            props.showRevokeDialog && (
                                <RevokeAppAuthDialog
                                    onDismiss={this._dismissRevokeAppDialog.bind(this)}
                                    onRevoke={this._revokeAppAuthorization.bind(this)}
                                    appName={this._appToRevoke.authorization.clientRegistration.registrationName}
                                />
                            )
                        );
                    }}
                </Observer>
            </Surface>
        );
    }

    private _revokeAppAuthorization = async () => {
        const actionTelemetryLogger = this._startActionCapture("Revoke App Authorization Triggered", { authId: this._appToRevoke.authorization });
        const restService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");

        try {
            const auth = this._appToRevoke.authorization;
            const authIds: string[] = [auth.authorization.authorizationId];

            if (this._appToRevoke.relatedAuthorizationIds && this._appToRevoke.relatedAuthorizationIds.length > 0) {
                authIds.push(...this._appToRevoke.relatedAuthorizationIds);
            }

            const response = await restService.getDataAsync<any>(revokeAuthDataProviderId, { authIds: authIds }, true);
            if (response.statusCode === 204) {
                this._showRevokeDialog.value = false;
                this._authData.removeAll((app: IAuthorizationViewModel) => {
                    return app.authorization.authorization.registrationId === auth.authorization.registrationId;
                });

                this.messagesService.addToast({
                    message: String.format(Resources.RevokeAppSuccess),
                    duration: 3500
                });
            } else {
                actionTelemetryLogger.captureError("Revoke App Authorization failed. Check product trace.");
            }
        } catch (error) {
            actionTelemetryLogger.captureError(error);
        }
    };

    private _dismissRevokeAppDialog() {
        this._showRevokeDialog.value = false;
    }

    private _onRevokeClick(item: IAuthorizationViewModel) {
        this._appToRevoke = item;
        this._showRevokeDialog.value = true;
    }

    private _getAppRowData(selectedRow: any) {
        this._selectedRowAppData = selectedRow.authorization;
        this._showApplicationDataPanel.value = true;
    }

    private _dismissApplicationDataPanel() {
        this._showApplicationDataPanel.value = false;
    }

    private _startActionCapture = (action: string, properties: { [key: string]: any } = {}): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.Authorizations, Feature.Authorizations, action, properties);
    };
}

Components.add("ms.vss-admin-web.profile-settings-authorizations-view-component", AuthorizationsViewComponent);
